// src/App.js

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, useParams } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import UsersList from './pages/UsersList';
import Organization from './pages/Organization';
import CreateOrganization from './pages/CreateOrganization';
import CreateGroup from './pages/CreateGroup';
import GroupsList from './pages/GroupsList';
import Group from './pages/Group';
import AppAdmin from './pages/AppAdmin';
import Login from './pages/Login';
import Signup from './pages/Signup';
import User from './pages/User';
import UploadVerses from './pages/UploadVerses';
import TagPage from './pages/TagPage';
import CategoryPage from './pages/CategoryPage';
import About from './pages/About';
import AddDisciple from './pages/AddDisciple';
import ViewDisciple from './pages/ViewDisciple';
import ListenConnectHelp from './pages/ListenConnectHelp';
import AllDisciples from './pages/AllDisciples';
import ForgotPassword from './pages/ForgotPassword';
import SendMessage from './pages/SendMessage';
import DiscipleHistory from './pages/DiscipleHistory';
import { AuthProvider, useAuth } from './contexts/authContext';
import RequireAuth from './components/RequireAuth';
import EditVerse from './pages/EditVerse';
import Reminders from './pages/Reminders';
import LandingPage from './pages/LandingPage';
import HeaderController from './components/HeaderController';
import OrganizationsList from './pages/OrganizationsList';
import Partners from './pages/Partners';
import NotificationHandler from './components/NotificationHandler';
import { requestMessagingToken } from './firebase';
import ReminderQueueTester from './pages/ReminderQueueTester';
import RemindersOverview from './pages/RemindersOverview';
import OrganizationMetrics from './pages/OrganizationMetrics';
import Footer from './components/Footer';
import Home from './pages/Home';
import ShareStory from './pages/ShareStory';
import FCMTester from './components/FCMTester';
import Welcome from './pages/Welcome';
import VerseCleanupTool from './components/VerseCleanupTool';
import MySpiritualJourney from './pages/MySpiritualJourney';
import BiscuitTrail from './components/BiscuitTrail';
import TrainingModules from './components/TrainingModules';
import HowToUseTOM from './components/HowToUseTOM';
import ContentTracks from './pages/ContentTracks';
import CreateContentTrack from './pages/CreateContentTrack';
import EditContentTrack from './pages/EditContentTrack';
import ViewContentTrack from './pages/ViewContentTrack';
import OrganizationContentTracks from './pages/OrganizationContentTracks';
import ContentPieceUploader from './components/ContentPieceUploader';
import CreateContentResource from './pages/CreateContentResource';
import CreateChildrenResource from './pages/CreateChildrenResource';
import EditChildrenResource from './pages/EditChildrenResource';
import EditContentResource from './pages/EditContentResource';
import UploadContentResources from './pages/UploadContentResources';
import DiscipleMenu from './pages/DiscipleMenu';
import MentoringNotesPage from './pages/MentoringNotesPage';
import SuggestedVersesPage from './pages/SuggestedVersesPage';
import SuggestedContent from './pages/SuggestedContent';
import SendContentMessage from './pages/SendContentMessage';
import FaithJourneyPage from './pages/FaithJourneyPage';
import BrowseContentTracks from './pages/BrowseContentTracks';
import SearchVerseSystem from './components/searchVerseSystem';
import SearchBibleVerses from './pages/SearchBibleVerses';
import EnrichNewVerses from './pages/EnrichNewVerses';
import PrayersManagement from './pages/prayersManagement';
import AddPrayer from './pages/AddPrayer';
import EditPrayer from './pages/EditPrayer';
import BulkPrayerUpload from './pages/BulkPrayerUpload';
import MyCommunity from './pages/MyCommunity';
import JoinOrganizationPage from './pages/JoinOrganization';
import DiscoverCommunities from './pages/DiscoverCommunities';
import AcceptInvitation from './pages/AcceptInvitation';
import ReminderDiagnosticsPage from './pages/ReminderDiagnosticsPage';
import RemindersByDisciple from './pages/RemindersByDisciple';
import AIManagement from './pages/AIManagement';
import Article from './pages/Article';
import BlankLanding from './pages/BlankLanding';
// Pricing page is now a tab on ForChurches — /pricing redirects to /for-churches?tab=pricing
import SubscriberOrganizations from './pages/admin/SubscriberOrganizations';
import ProductsManagement from './pages/admin/ProductsManagement';
import SubscriptionSuccess from './pages/organizations/SubscriptionSuccess';
import ReadTogether from './pages/ReadTogether';
import WelcomeMessagesManagement from './pages/admin/WelcomeMessagesManagement';
import SurveysManagement from './pages/admin/SurveysManagement';
import SurveyEdit from './pages/admin/SurveyEdit';
import SurveyQuestionsManagement from './pages/admin/SurveyQuestionsManagement';
import Marketplace from './pages/Marketplace';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import ForChurches from './pages/ForChurches';
import Onboarding from './pages/Onboarding';

function SubscriptionRedirect() {
  const { orgId } = useParams();
  return <Navigate to={orgId ? `/organization/${orgId}?tab=account` : '/organizations'} replace />;
}

function AppContent() {
  const { currentUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    requestMessagingToken()
      .then(token => {
        if (token) {
          console.log('Messaging initialized successfully');
        }
      })
      .catch(error => {
        console.error('Error initializing messaging:', error);
      });
  }, []);

  // Determine which component to show for root path based on domain
  const getRootComponent = () => {
    const hostname = window.location.hostname;
    
    // For tmrsrc.com, show blank landing (for articles)
    if (hostname === 'tmrsrc.com' || hostname === 'www.tmrsrc.com') {
      return <BlankLanding />;
    }
    
    // For localhost (development), show the app
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
      // If user is logged in, redirect to dashboard, otherwise show login
      if (currentUser) {
        return <Navigate to="/dashboard" replace />;
      }
      return <Navigate to="/login" replace />;
    }
    
    // For app.togetheronmission.org or the default Firebase domain, redirect appropriately
    if (hostname === 'app.togetheronmission.org' || 
        hostname === 'together-on-mission-dev.web.app' ||
        hostname === 'together-on-mission-dev.firebaseapp.com') {
      // If user is logged in, redirect to dashboard, otherwise show login
      if (currentUser) {
        return <Navigate to="/dashboard" replace />;
      }
      return <Navigate to="/login" replace />;
    }
    
    // Default: show blank landing (for other domains)
    return <BlankLanding />;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <HeaderController />
      <div className="pb-16">
            <Routes>
              {/* Public Routes */}
              <Route path="/landing" element={<LandingPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/pricing" element={<Navigate to="/for-churches?tab=pricing" replace />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/for-churches" element={<ForChurches />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfUse />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/join-organization" element={<JoinOrganizationPage />} />
              <Route path="/" element={getRootComponent()} />
              {/* Path-based tracking survives SMS/preview clients that strip ?query= */}
              <Route path="/article/:articleId/t/:trackPack" element={<Article />} />
              <Route path="/articles/:articleId/t/:trackPack" element={<Article />} />
              <Route path="/article/:articleId" element={<Article />} />
              <Route path="/articles/:articleId" element={<Article />} />

              {/* Semi-public: accessible after Stripe redirect (session_id is proof of payment) */}
              <Route path="/organization/:orgId/subscription/success" element={<SubscriptionSuccess />} />

              {/* Semi-public: create-organization handles its own auth (step-based wizard for new users) */}
              <Route path="/create-organization" element={<CreateOrganization />} />

              {/* Protected Routes */}
              <Route element={<RequireAuth />}>
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/my-community" element={<MyCommunity />} />
                <Route path="/discover-communities" element={<DiscoverCommunities />} />
                <Route path="/accept-invitation" element={<AcceptInvitation />} />
                <Route path="/users" element={<UsersList />} />
                <Route path="/organizations" element={<OrganizationsList />} />
                <Route path="/organization/:orgId" element={<Organization />} />
                <Route path="/organization/:orgId/subscription" element={<SubscriptionRedirect />} />
                <Route path="/organization/:orgId/invite" element={<Organization />} />
                <Route path="/organization-metrics" element={<OrganizationMetrics />} />
                <Route path="/create-group" element={<CreateGroup />} />
                <Route path="/groups" element={<GroupsList />} />
                <Route path="/group/:groupId" element={<Group />} />
                <Route path="/admin" element={<AppAdmin />} />
                <Route path="/admin/subscriber-organizations" element={<SubscriberOrganizations />} />
                <Route path="/admin/products-management" element={<ProductsManagement />} />
                <Route path="/admin/welcome-messages" element={<WelcomeMessagesManagement />} />
                <Route path="/admin/surveys" element={<SurveysManagement />} />
                <Route path="/admin/surveys/new" element={<SurveyEdit />} />
                <Route path="/admin/surveys/:surveyId/edit" element={<SurveyEdit />} />
                <Route path="/admin/survey-questions" element={<SurveyQuestionsManagement />} />
                <Route path="/user/:userId" element={<User />} />
                <Route path="/upload-verses" element={<UploadVerses />} />
                <Route path="/tags/:tagName" element={<TagPage />} />
                <Route path="/categories/:categoryName" element={<CategoryPage />} />
                <Route path="/add-disciple" element={<AddDisciple />} />
                <Route path="/view-disciple/:discipleId" element={<ViewDisciple />} />
                <Route path="/view-disciple/:discipleId/listen-connect-help" element={<ListenConnectHelp />} />
                <Route path="/read-together/:discipleId" element={<ReadTogether />} />
                <Route path="/all-disciples" element={<AllDisciples />} />
                <Route path="/marketplace" element={<Marketplace />} />
                <Route path="/send-message" element={<SendMessage />} />
                <Route path="/disciple-history/:discipleId" element={<DiscipleHistory />} />
                <Route path="/app-admin" element={<AppAdmin />} />
                <Route path="/edit-verse/:verseId" element={<EditVerse />} />
                <Route path="/reminders" element={<Reminders />} />
                <Route path="/reminder-queue-tester" element={<ReminderQueueTester />} />
                <Route path="/reminder-diagnostics" element={<ReminderDiagnosticsPage />} />
                <Route path="/reminders-by-disciple" element={<RemindersByDisciple />} />
                <Route path="/reminders-overview" element={<RemindersOverview />} />
                <Route path="/ai-management" element={<AIManagement />} />
                <Route path="/share-story" element={<ShareStory />} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/verse-cleanup" element={<VerseCleanupTool />} />
                <Route path="/my-spiritual-journey" element={<MySpiritualJourney />} />
                <Route path="/biscuit-trail" element={<BiscuitTrail />} />
                <Route path="/training-modules" element={<TrainingModules />} />
                <Route path="/how-to-use-tom" element={<HowToUseTOM />} />

                {/* Content Track Routes */}
                <Route 
                  path="/content-tracks" 
                  element={<ContentTracks />}
                />
                <Route 
                  path="/content-tracks/create" 
                  element={<CreateContentTrack />}
                />
                {/* Content Resource Routes - Must come before general track route */}
                <Route path="/content-resources/create" element={<CreateContentResource />} />
                <Route path="/content-tracks/:trackId/resources/children/create" element={<CreateChildrenResource />} />
                <Route path="/content-tracks/:trackId/resources/create" element={<CreateContentResource />} />
                <Route 
                  path="/content-tracks/:trackId/resources/upload" 
                  element={<UploadContentResources />} 
                />
                <Route 
                  path="/content-tracks/:trackId/resources/:resourceId/children/edit" 
                  element={<EditChildrenResource />} 
                />
                <Route 
                  path="/content-tracks/:trackId/resources/:resourceId/edit" 
                  element={<EditContentResource />} 
                />

                <Route 
                  path="/content-tracks/:trackId/edit" 
                  element={<EditContentTrack />}
                />
                <Route 
                  path="/content-tracks/:trackId" 
                  element={<ViewContentTrack />}
                />

                {/* Organization Content Track Routes */}
                <Route 
                  path="/organizations/:orgId/content-tracks" 
                  element={<OrganizationContentTracks />}
                />
                <Route 
                  path="/organizations/:orgId/content-tracks/create" 
                  element={<CreateContentTrack />}
                />

                {/* Development Routes */}
                {process.env.NODE_ENV === 'development' && (
                  <Route path="/reminder-queue-tester" element={<ReminderQueueTester />} />
                )}
                <Route 
                  path="/content-tracks/:trackId/resources/upload" 
                  element={<UploadContentResources />} 
                />
                <Route 
                  path="/content-tracks/:trackId/resources/:resourceId/edit" 
                  element={<EditContentResource />} 
                />

                {/* Disciple Menu Route */}
                <Route 
                  path="/disciple-menu/:discipleId" 
                  element={<DiscipleMenu />} 
                />

                {/* Mentoring Notes Route */}
                <Route 
                  path="/mentoring-notes/:discipleId" 
                  element={<MentoringNotesPage />} 
                />

                {/* Suggested Verses Route */}
                <Route 
                  path="/suggested-verses/:discipleId" 
                  element={<SuggestedVersesPage />} 
                />

                {/* Suggested Content Route */}
                <Route 
                  path="/suggested-content/:discipleId" 
                  element={<SuggestedContent />} 
                />

                {/* Send Content Message Route */}
                <Route 
                  path="/send-content-message/:discipleId/:resourceId" 
                  element={<SendContentMessage />} 
                />

                {/* Faith Journey Route */}
                <Route 
                  path="/faith-journey/:discipleId"
                  element={<FaithJourneyPage />}
                />

                {/* Browse Content Tracks Route */}
                <Route 
                  path="/browse-content-tracks/:discipleId"
                  element={<BrowseContentTracks />}
                />

                {/* Search Verse System Route */}
                <Route path="/search-verses-system" element={<SearchVerseSystem />} />

                {/* Search Bible Verses Route */}
                <Route path="/search-bible-verses" element={<SearchBibleVerses />} />
                {/* Enrich Newly Added Verses Route */}
                <Route path="/enrich-new-verses" element={<EnrichNewVerses />} />

                {/* Prayers Management Routes */}
                <Route path="/prayers-management" element={<PrayersManagement />} />
                <Route path="/add-prayer" element={<AddPrayer />} />
                <Route path="/edit-prayer/:id" element={<EditPrayer />} />
                <Route path="/bulk-prayer-upload" element={<BulkPrayerUpload />} />
              </Route>
            </Routes>
            {currentUser && !location.pathname.startsWith('/article') && !location.pathname.startsWith('/articles') && location.pathname !== '/' && <Footer />}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
      <NotificationHandler />
    </AuthProvider>
  );
}

export default App;
